import { useEffect, useLayoutEffect, useState } from "react";
import { EncabezadoOrigen } from "../../Components/EncabezadoOrigen/EncabezadoOrigen";
import { SegStepper } from "../../Components/SegUI/SegStepper/SegStepper";
import { InicioNormal } from "../../Components/Slides/Auto/Cotizacion/InicioNormal";
import { DatosCotizacion } from "../../Components/Slides/Comunes/Cotizacion/DatosCotizacion";
import styled from "@emotion/styled";
import { useCompania } from "../../Routers/AppRouter/cotizadorDeCompania";
import { useNavigate } from "react-router-dom";
import { obtenerParametros, obtenerDescripciones } from "../../Services/API/bff/auto";
import { useCotizacionStore } from "../../Services/Providers/CotizacionStore";
import { configEmpresaDeParametros } from "../../Services/Providers/configEmpresaDeParametros";
import { useDescripcionesStore } from "../../Services/Providers/DescripcionesStore";

import { TelefonosCompañia, telefonos } from "../../Assets/telefonosCompanias";
import { useConfiguracionEmpresa } from "../../Services/Providers/useConfiguracionEmpresa";
import useSEOConfig from "../../hooks/seo/useSEOConfig";
import { useConfig } from "../../context/ConfigContext";
import { buscarTextoSeo } from "../../utils/buscarTextoSeo";
import { CookiesComponent } from "../../Components/otros/CookiesComponent";
import { SeoContenido } from "./SeoContenido";
import { debeMostrarBloqueSeo } from "../../utils/debeMostrarBloqueSeo";
import { useTextosCotizadorAuto } from "../../Services/ventaPresencial/textosAsesor";
import { quitarMarcaDeVendedor } from "../../Services/ventaPresencial/marcaVendedor";
import { esLandingDeVendedor } from "../../Services/ventaPresencial/rutaVendedor";

export interface IPasos {
  step: number;
  subStep: number;
}

export interface CotizadorProps {
  step?: number;
  subStep?: number;
  esHome?: boolean;
  /** Lo renderiza la landing de venta presencial (/auto/vendedor): no es una entrada pública. */
  desdeVendedor?: boolean;
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  /* min-height: 100vh; */
`;

const Loading = styled.section`
    position: fixed;
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
    background: #fff;
`;
/**
 * Entrada al cotizador de Auto. Si es una entrada pública al paso de patente y la pestaña tenía una
 * cotización del asesor (US 23866), primero la pasa a cliente y recién después monta el cotizador: así
 * todos sus efectos y pantallas arrancan con la cotización ya corregida (si se corrigiera con el
 * cotizador montado, sus efectos podrían volver a guardar la versión anterior, y las pantallas que leen
 * el store la verían un cuadro tarde). Al asesor nunca le pasa: los botones de su recorrido vuelven a
 * /auto/vendedor (inicioCotizacion), que monta el cotizador con `desdeVendedor`.
 */
export const Cotizador = (props: CotizadorProps) => {
  const { step = 0, desdeVendedor = false } = props;
  const setCotizacionValues = useCotizacionStore((s) => s.setCotizacionValues);
  const [quitandoMarca, setQuitandoMarca] = useState(
    () => step === 0 && !desdeVendedor && esLandingDeVendedor(useCotizacionStore.getState().cotizacionValues.landingUrl)
  );
  // Antes de pintar: la vuelta en null no llega a verse.
  useLayoutEffect(() => {
    if (!quitandoMarca) return;
    setCotizacionValues(quitarMarcaDeVendedor(useCotizacionStore.getState().cotizacionValues, document.location.href));
    setQuitandoMarca(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (quitandoMarca) return null;
  return <CotizadorAuto {...props} />;
};

const CotizadorAuto = ({ step = 0, subStep = 1, esHome = false }: CotizadorProps) => {
  const compania = useCompania();
  const nav = useNavigate();
  const { isLoading, config: envConf } = useConfig()
  const textoSeo = buscarTextoSeo("/cotizador-de-seguros-auto", envConf.pais);

  const Icon = styled.svg`
    color: #343434 !important;
  `;
  const referer = document.referrer;
  const landingUrl = document.location.href;

  const [steps, setSteps] = useState<IPasos>({ step, subStep });
  const { cotizacionValues, setCotizacionValues } = useCotizacionStore();
  const textos = useTextosCotizadorAuto("auto");

  const {
    descripcionesValues,
    setDescripcionesValues,
  } = useDescripcionesStore();

  const [paramsCargando, setParamsCargando] = useState(false);
  const { origen, config, setConfig } = useConfiguracionEmpresa();
  const mostrarSeo = debeMostrarBloqueSeo({ esHome, pais: envConf.pais, origen });
  const [phones, setPhones] = useState<TelefonosCompañia[]>(telefonos);
  const [justifyContent, setJustifyContent] = useState(
    "justify-content-center"
  );

  useSEOConfig({
    title: textoSeo?.title ?? "Auto",
    description: textoSeo?.description,
    tituloCompleto: !!textoSeo,
    marcaBlanca: (origen !== "SEGURARSE") ? config.origen.charAt(0).toUpperCase() +
      origen.slice(1).toLowerCase() : undefined,
    robots: (origen !== "SEGURARSE") ? "noindex, nofollow" : ""
  })


  useEffect(() => {
    function ajustarJustifyContent() {
      if (window.innerWidth >= 768) {
        setJustifyContent("col-md-3 text-md-end align-self-center");
      } else {
        setJustifyContent(
          "d-flex align-items-center justify-content-center mt-3"
        );
      }
    }

    // Llamar a la función al cargar la página y al cambiar el tamaño de la ventana
    ajustarJustifyContent();
    window.addEventListener("resize", ajustarJustifyContent);

    // Limpieza del listener cuando el componente se desmonta
    return () => {
      window.removeEventListener("resize", ajustarJustifyContent);
    };
  }, []);


  useEffect(() => {
    setSteps((prev) => {
      return {
        ...prev,
        step: step,
        subStep: subStep,
      };
    });
  }, [step, subStep]);

  useEffect(() => {
    if (!cotizacionValues.parametrosIniciales || cotizacionValues.parametrosIniciales.rubro !== "AUTO") {
      setParamsCargando(true);
      const fetchDataParams = async () => {
        const response: any = await obtenerParametros({ rubro: "AUTO" });
        if (response) {
          // Cambio de rubro (p. ej. se venía cotizando una moto): el vehículo
          // guardado no sirve para auto, se limpia para no arrastrarlo.
          const rubroAnterior = cotizacionValues?.parametrosIniciales?.rubro;
          const cambioDeRubro = !!rubroAnterior && rubroAnterior !== "AUTO";
          setCotizacionValues({
            ...cotizacionValues,
            ...(cambioDeRubro && {
              marca: undefined,
              modelo: undefined,
              version: undefined,
              año: undefined,
              esAntiguo: undefined,
              patente: undefined,
            }),
            parametrosIniciales: response.data,
            referer: referer,
            landingUrl: landingUrl,
          });
          // Misma marca del origen que usa /retomar (configEmpresaDeParametros).
          const [configOrigen, origenNormalizado] = configEmpresaDeParametros(response.data);
          setConfig(configOrigen, origenNormalizado);
        }
      };

      fetchDataParams().then(() => {
        setParamsCargando(false);
      });
    }
  }, []);

  useEffect(() => {
    if (origen !== "SEGURARSE") {
      const favicon: any = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.href = config.images.favicon
      } else {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = config.images.favicon || "";
        document.head.appendChild(link);
      }
    }
  }, [origen])

  useEffect(() => {
    if (
      cotizacionValues.parametrosIniciales &&
      descripcionesValues?.length === 0
    ) {
      const fetchDataDesc = async (
        rubro: string,
        origen: string,
        tipoCotizador: string
      ) => {
        const response: any = await obtenerDescripciones(
          rubro,
          origen,
          tipoCotizador
        );
        if (response) {
          setDescripcionesValues(response.data.api_data);
        }
      };
      fetchDataDesc(
        cotizacionValues.parametrosIniciales.rubro,
        cotizacionValues.parametrosIniciales.origen,
        cotizacionValues.parametrosIniciales.tipoCotizador
      );
    }
  }, [cotizacionValues.parametrosIniciales]);

  useEffect(() => {
    if (cotizacionValues.contactoId) {
      setCotizacionValues({
        ...cotizacionValues,
        contactoId: undefined,
      });
    }
  }, [
    cotizacionValues.patente,
    cotizacionValues.marca,
    cotizacionValues.modelo,
    cotizacionValues.año,
    cotizacionValues.version,
    cotizacionValues.nombre,
    cotizacionValues.apellido,
    cotizacionValues.email,
    cotizacionValues.telefono,
    cotizacionValues.fechaNac,
    cotizacionValues.genero,
    cotizacionValues.localidad,
    cotizacionValues.provincia,
  ]);


  /**
   * Dos pasos: la patente, y después todo lo que falta en una sola pantalla.
   * Los 13 slides de una pregunta por pantalla quedaron reemplazados por
   * DatosCotizacion; sus URLs redirigen ahí (ver ArgRouter / UYRouter).
   */
  const renderVista = () => {
    switch (steps?.step) {
      case 0:
        return <InicioNormal config={config} setSteps={setSteps} />;
      case 1:
        return (
          <DatosCotizacion rubro="auto" config={config} incluirVehiculo={true} />
        );
      default:
        return <h1>loading</h1>;
    }
  };

  const tel = phones.filter((t: TelefonosCompañia) => {
    if (t.clave === compania) {
      return t;
    }
  });


  if (paramsCargando) {
    return (
      <Loading>
        <div className='d-flex flex-column justify-content-center align-items-center h-100'>
          <p className=''><strong>Cargando</strong></p>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Loading>
    )
  }

  return (
    <>
      <PageContainer>
        <EncabezadoOrigen origen={origen} config={config}>
                  {compania && tel.length > 0 && (
                    <div className={`${justifyContent}`}>
                      {" "}
                      {/* Alinea el contenido al centro vertical y a la derecha */}
                      <a
                        href={"tel:" + tel[0]?.telefono}
                        className="tel align-items-center gap-2"
                      >
                        <Icon className="icon">
                          <use xlinkHref="#tel" />
                        </Icon>
                        {tel[0]?.telefono}
                      </a>
                    </div>
                  )}
        </EncabezadoOrigen>

        <div>
          <SegStepper
            icon="auto"
            label={textos.tituloCotizador}
            stepActive={steps.step === 0 ? 1 : 2}
            isStepper={true}
            finalStep='$'
            bgColor={origen !== "SEGURARSE" ? config.colors.segu_stepper : undefined}
            iconColor={origen !== "SEGURARSE" ? config.colors.icons : undefined}
            lineColor={origen !== "SEGURARSE" ? config.colors.icons : undefined}
            labelColor={origen !== "SEGURARSE" ? config.colors.icons : undefined}
            stepInactiveBgColor={origen !== "SEGURARSE" ? config.colors.icons : undefined}
            stepActiveBorderColor={origen !== "SEGURARSE" ? config.colors.icons : undefined}
            stepActiveTextColor={origen !== "SEGURARSE" ? config.colors.icons : undefined}
          />
        </div>
        {(paramsCargando && isLoading) ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {renderVista()}
            <SeoContenido visible={mostrarSeo} />
          </>
        )}

        {
          config.config.cookies &&
          <CookiesComponent />
        }
      </PageContainer>
    </>
  );
};